// به‌روزرسانی زنده نوبت‌ها (FLOW-03؛ SignalR hub `scheduling` — C-AGENT-346): اتصال LongPolling تا توکن مستأجر فقط در سرآیند
// Authorization (از حافظه نشست، هرگز localStorage یا Query String) برود. پیام `appointmentsChanged` فقط شناسه شعبه و نوبت‌ها را
// دارد (بدون داده شخصی)؛ صفحه‌ها داده را دوباره از API می‌خوانند. اتصال قابل تزریق است تا تست‌ها رویداد ساختگی بفرستند.
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

export interface AppointmentsChanged {
  branchId: string;
  appointmentIds: string[];
}

export interface SchedulingRealtimeConnection {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  onChanged: (handler: (message: AppointmentsChanged) => void) => void;
}

export interface SchedulingRealtimeOptions {
  url: string;
  getToken: () => string | undefined;
}

export type SchedulingRealtimeFactory = (options: SchedulingRealtimeOptions) => SchedulingRealtimeConnection;

export const HUB_PATH = '/hubs/scheduling';

/** اتصال واقعی SignalR (LongPolling، تلاش دوباره خودکار). */
export const signalRFactory: SchedulingRealtimeFactory = ({ url, getToken }) => {
  const connection = new HubConnectionBuilder()
    .withUrl(url, {
      transport: HttpTransportType.LongPolling,
      accessTokenFactory: () => getToken() ?? '',
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();
  return {
    start: () => connection.start(),
    stop: () => connection.stop(),
    onChanged: (handler) => connection.on('appointmentsChanged', handler),
  };
};

type Listener = (message: AppointmentsChanged) => void;

interface RealtimeValue {
  subscribe: (listener: Listener) => () => void;
}

const RealtimeContext = createContext<RealtimeValue | null>(null);

export interface SchedulingRealtimeProviderProps {
  children: ReactNode;
  apiBaseUrl: string;
  factory?: SchedulingRealtimeFactory | false;
}

/** یک اتصال برای کل پوسته؛ با جابه‌جایی مستأجر بسته و دوباره باز می‌شود. */
export function SchedulingRealtimeProvider({ children, apiBaseUrl, factory = signalRFactory }: SchedulingRealtimeProviderProps) {
  const { session } = useTenantSession();
  const access = useAccess();
  const listeners = useRef(new Set<Listener>());
  const token = useRef<string | undefined>(undefined);
  token.current = session?.accessToken;
  const create = factory === false ? null : factory;
  const enabled = create !== null && access.can('sch.appointment.view');
  const tenantId = session?.tenantId;

  useEffect(() => {
    if (!enabled || !tenantId || !create) {
      return undefined;
    }
    const base = apiBaseUrl || (typeof window === 'undefined' ? '' : window.location.origin);
    const connection = create({ url: `${base}${HUB_PATH}`, getToken: () => token.current });
    connection.onChanged((message) => {
      for (const listener of listeners.current) {
        listener(message);
      }
    });
    let active = true;
    connection.start().catch(() => {
      // نبود اتصال زنده صفحه را از کار نمی‌اندازد؛ داده با بازخوانی دوره‌ای و پس از هر اقدام تازه می‌شود.
      active = false;
    });
    return () => {
      if (active) {
        void connection.stop().catch(() => undefined);
      }
    };
  }, [enabled, tenantId, apiBaseUrl, create]);

  const value = useMemo<RealtimeValue>(
    () => ({
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => {
          listeners.current.delete(listener);
        };
      },
    }),
    [],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/** گوش دادن به تغییر نوبت‌ها (برگه روز، تقویم، جزئیات). */
export function useAppointmentsChanged(listener: Listener): void {
  const context = useContext(RealtimeContext);
  const latest = useRef(listener);
  latest.current = listener;
  useEffect(() => context?.subscribe((message) => latest.current(message)), [context]);
}
